import { FC, useState } from 'react';
import { cn } from '@new/lib';
import { Checkbox } from '@new/common/ui/Checkbox/Checkbox';
import { Button } from '@new/common/ui/Button/Button';
import { useCan } from '@new/casl/hooks/useCan';
import { useVideoCallContext } from '../../../../../contexts/VideoCallProvider';
import { useConfirmHearingMutation as useConfirmHearing } from '@teachur/http-client';
import {
  INSTRUCTOR_CONFIRMATION_TEXT,
  STUDENT_PREAMBLE_TEXT,
  STUDENT_CONFIRMATIONS,
} from '../../constants';

export const CourseHearingConfirmation: FC = () => {
  const isInstructor = useCan('manage', 'CourseHearing');

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
      <div className="flex items-center justify-center min-h-[60vh] p-8">
        <div className="bg-white rounded-2xl shadow-lg max-w-2xl w-full p-8">
          {isInstructor ? <InstructorConfirmation /> : <StudentConfirmation />}
        </div>
      </div>
    </div>
  );
};

interface SubmitButtonProps {
  isValid: boolean;
  isPending: boolean;
  onSubmit: () => void;
}

const SubmitButton: FC<SubmitButtonProps> = ({ isValid, isPending, onSubmit }) => (
  <div className="mt-8 flex justify-center">
    <Button
      onClick={onSubmit}
      disabled={!isValid || isPending}
      variant="primary"
      className={cn('w-48', !isValid && 'opacity-50 cursor-not-allowed')}
    >
      {isPending ? 'Submitting...' : 'Submit'}
    </Button>
  </div>
);

const InstructorConfirmation: FC = () => {
  const { videoCallId, setVideoCall } = useVideoCallContext();
  const confirmMutation = useConfirmHearing();
  const [confirmed, setConfirmed] = useState(false);

  const handleSubmit = () => {
    if (!confirmed || !videoCallId) return;
    confirmMutation.mutate(videoCallId, {
      onSuccess: (updatedVideoCall) => setVideoCall(updatedVideoCall),
    });
  };

  return (
    <>
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          <Checkbox value={confirmed} onChange={setConfirmed} />
        </div>
        <p className="text-sm text-neutral-600 leading-relaxed">{INSTRUCTOR_CONFIRMATION_TEXT}</p>
      </div>

      <SubmitButton
        isValid={confirmed}
        isPending={confirmMutation.isPending}
        onSubmit={handleSubmit}
      />
    </>
  );
};

const StudentConfirmation: FC = () => {
  const { videoCallId, setVideoCall } = useVideoCallContext();
  const confirmMutation = useConfirmHearing();
  const [confirmed, setConfirmed] = useState(false);

  const handleSubmit = () => {
    if (!confirmed || !videoCallId) return;
    confirmMutation.mutate(videoCallId, {
      onSuccess: (updatedVideoCall) => setVideoCall(updatedVideoCall),
    });
  };

  return (
    <>
      <div className="space-y-6">
        <p className="text-sm text-neutral-600 leading-relaxed whitespace-pre-line">
          {STUDENT_PREAMBLE_TEXT}
        </p>

        <ul className="space-y-2 list-disc list-inside">
          {STUDENT_CONFIRMATIONS.map((text, index) => (
            <li key={index} className="text-sm text-neutral-600 leading-relaxed">
              {text}
            </li>
          ))}
        </ul>

        <div className="flex items-start gap-3">
          <div className="pt-0.5">
            <Checkbox value={confirmed} onChange={setConfirmed} />
          </div>
          <p className="text-sm text-neutral-600 leading-relaxed">I agree to all of the above.</p>
        </div>
      </div>

      <SubmitButton
        isValid={confirmed}
        isPending={confirmMutation.isPending}
        onSubmit={handleSubmit}
      />
    </>
  );
};
